import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { CSSProperties } from 'react'
import { PiArrowDown, PiArrowRight, PiBrain, PiListChecks, PiSparkle } from 'react-icons/pi'
import { DarkModeToggle } from '../components/shared/DarkModeToggle'
import { Logo } from '../components/shared/Logo'
import { Button } from '../components/ui/button'
import { useTheme } from '../contexts/ThemeContext'
import { setUnlockState } from '../utils/environment'
import { withAlpha } from '../utils/themeUtils'

export function LandingPage() {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const navigate = useNavigate()

  const openOwnerSignIn = () => {
    setUnlockState(true)
    navigate('/login')
  }

  return (
    <main className="min-h-dvh overflow-hidden" style={{ backgroundColor: colors.background, color: colors.text }}>
      <header className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6 sm:py-5">
        <a href="#top" className="flex min-h-11 items-center gap-2.5 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" style={{ '--tw-ring-color': colors.primary } as CSSProperties}>
          <Logo size={34} />
          <span className="text-lg font-semibold tracking-[-0.025em]">Tydy</span>
        </a>
        <div className="flex items-center gap-1 sm:gap-2">
          <DarkModeToggle />
          <Button variant="ghost" size="sm" onClick={openOwnerSignIn}>{t('landing.private.ownerSignIn')}</Button>
        </div>
      </header>

      <section id="top" className="mx-auto grid max-w-6xl items-center gap-12 px-4 pb-20 pt-14 sm:px-6 sm:pb-28 sm:pt-20 lg:grid-cols-[0.82fr_1.18fr] lg:gap-16 lg:pb-32 lg:pt-24">
        <div className="max-w-xl">
          <p className="flex items-center gap-2 text-sm font-medium" style={{ color: colors.textSecondary }}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colors.primary }} />
            {t('landing.new.status')}
          </p>
          <h1 className="mt-6 text-[clamp(3.1rem,11vw,5.6rem)] font-semibold leading-[0.92] tracking-[-0.07em]">
            {t('landing.new.headline')}
          </h1>
          <p className="mt-7 max-w-lg text-lg leading-8 sm:text-xl sm:leading-9" style={{ color: colors.textSecondary }}>
            {t('landing.new.intro')}
          </p>
          <a href="#journey" className="mt-8 inline-flex min-h-11 items-center gap-2 rounded-xl text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" style={{ color: colors.primary, '--tw-ring-color': colors.primary } as CSSProperties}>
            {t('landing.new.followThought')}<PiArrowDown className="h-4 w-4" />
          </a>
        </div>

        <ContextJourney />
      </section>

      <section id="journey" className="border-y" style={{ borderColor: withAlpha(colors.border, 'A0'), backgroundColor: colors.surface }}>
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-20 sm:px-6 sm:py-28 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20">
          <div>
            <p className="text-sm font-semibold" style={{ color: colors.primary }}>{t('landing.new.laterEyebrow')}</p>
            <h2 className="mt-4 max-w-md text-3xl font-semibold leading-[1.08] tracking-[-0.045em] sm:text-5xl">
              {t('landing.new.laterTitle')}
            </h2>
          </div>
          <div className="divide-y" style={{ borderColor: withAlpha(colors.border, 'A0') }}>
            <JourneyDestination icon={PiListChecks} title={t('navigation.today')} copy={t('landing.new.todayCopy')} />
            <JourneyDestination icon={PiBrain} title={t('navigation.remember')} copy={t('landing.new.rememberCopy')} />
            <JourneyDestination icon={PiSparkle} title={t('navigation.signals')} copy={t('landing.new.signalsCopy')} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
        <div className="grid gap-8 lg:grid-cols-[1.25fr_0.75fr] lg:items-end lg:gap-16">
          <blockquote className="max-w-3xl text-3xl font-medium leading-[1.16] tracking-[-0.04em] sm:text-5xl sm:leading-[1.08]">
            “{t('landing.new.builderQuote')}”
          </blockquote>
          <div className="border-l-2 pl-5" style={{ borderColor: colors.primary }}>
            <p className="text-sm leading-6" style={{ color: colors.textSecondary }}>{t('landing.new.builderBody')}</p>
            <p className="mt-4 text-sm font-semibold">— Kharee</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-8 sm:px-6 sm:pb-12">
        <div className="flex flex-col gap-5 border-t py-8 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: withAlpha(colors.border, 'A0') }}>
          <div>
            <p className="font-semibold">{t('landing.private.title')}</p>
            <p className="mt-1 max-w-2xl text-sm leading-6" style={{ color: colors.textSecondary }}>{t('landing.private.description')}</p>
          </div>
          <Button variant="secondary" className="shrink-0" onClick={openOwnerSignIn}>
            {t('landing.private.ownerSignIn')}<PiArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>
    </main>
  )
}

function ContextJourney() {
  const { t } = useTranslation()
  const { colors } = useTheme()

  return (
    <figure className="relative mx-auto w-full max-w-2xl lg:mx-0">
      <div className="absolute -inset-10 -z-0 rounded-full blur-3xl" style={{ backgroundColor: withAlpha(colors.primary, '12') }} />
      <div className="relative overflow-hidden rounded-[30px] border p-4 shadow-[0_28px_90px_-52px_rgba(22,32,42,0.55)] sm:p-6" style={{ borderColor: withAlpha(colors.border, 'D0'), backgroundColor: colors.surface }}>
        <div className="flex items-center justify-between gap-3 border-b pb-4" style={{ borderColor: withAlpha(colors.border, '90') }}>
          <p className="text-sm font-semibold">{t('landing.new.journey.capture')}</p>
          <p className="text-xs" style={{ color: colors.textSecondary }}>{t('landing.new.journey.example')}</p>
        </div>

        <blockquote className="py-7 text-xl font-medium leading-8 tracking-[-0.02em] sm:px-3 sm:text-2xl sm:leading-9">
          “{t('landing.new.journey.captureText')}”
        </blockquote>

        <div className="relative ml-3 border-l pb-1 pl-6 sm:ml-6 sm:pl-8" style={{ borderColor: withAlpha(colors.primary, '66') }}>
          <span className="absolute -left-[0.32rem] top-1 h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colors.primary }} />
          <p className="text-xs font-semibold" style={{ color: colors.primary }}>{t('landing.new.journey.retained')}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <JourneyRecord label={t('landing.new.journey.observation')} title={t('landing.new.journey.observationTitle')} />
            <JourneyRecord label={t('landing.new.journey.connectedSubjects')} title={t('landing.new.journey.subjects')} />
          </div>
          <div className="mt-4 flex items-start gap-3 rounded-2xl px-4 py-3" style={{ backgroundColor: colors.surfaceAlt || withAlpha(colors.primary, '0D') }}>
            <PiSparkle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: colors.primary }} />
            <div>
              <p className="text-sm font-semibold">{t('landing.new.journey.receipt')}</p>
              <p className="mt-1 text-sm leading-5" style={{ color: colors.textSecondary }}>{t('landing.new.journey.receiptDetail')}</p>
            </div>
          </div>
        </div>
      </div>
      <figcaption className="mt-3 text-center text-xs" style={{ color: colors.textSecondary }}>{t('landing.new.journey.caption')}</figcaption>
    </figure>
  )
}

function JourneyRecord({ label, title }: { label: string; title: string }) {
  const { colors } = useTheme()
  return (
    <div className="rounded-2xl border px-4 py-3" style={{ borderColor: withAlpha(colors.border, 'A0'), backgroundColor: colors.background }}>
      <p className="text-xs font-medium" style={{ color: colors.textSecondary }}>{label}</p>
      <p className="mt-1 text-sm font-semibold">{title}</p>
    </div>
  )
}

function JourneyDestination({ icon: Icon, title, copy }: { icon: typeof PiBrain; title: string; copy: string }) {
  const { colors } = useTheme()
  return (
    <article className="grid gap-3 py-6 first:pt-0 last:pb-0 sm:grid-cols-[3rem_8rem_1fr] sm:items-start">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ backgroundColor: withAlpha(colors.primary, '12'), color: colors.primary }}><Icon className="h-5 w-5" /></span>
      <h3 className="pt-2.5 font-semibold">{title}</h3>
      <p className="text-sm leading-6 sm:pt-2" style={{ color: colors.textSecondary }}>{copy}</p>
    </article>
  )
}

export default LandingPage
